import { IconSearch } from "@tabler/icons-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/atomic/Input";
import { ScrollArea, ScrollBar } from "@/components/atomic/ScrollArea";
import { AttributesList } from "@/components/ui/chat/attribute-filters/AttributesList";
import { ChatAttributeFilter, ChatAttributeFilters } from "@/components/ui/chat/chatFilterUtils";
import { VocSearchTagFilter } from "@/components/ui/VocSearchTagFilter";
import { useAttributes } from "@/hooks/useAttributes";
import { useWorkspaces } from "@/hooks/useWorkspaces";

interface VocSearchAdvancedFilterTabProps {
  attributeFilters: ChatAttributeFilters;
  tagIds: string[];
  onUpdateAttributeFilter: (attributeName: string, filter: ChatAttributeFilter | null) => void;
  onToggleTagId: (tagId: string) => void;
}

export const VocSearchAdvancedFilterTab: React.FC<VocSearchAdvancedFilterTabProps> = ({
  attributeFilters,
  tagIds,
  onUpdateAttributeFilter,
  onToggleTagId,
}) => {
  const { t } = useTranslation("voiceek_ui");
  const { sessionCurrentWorkspaceId } = useWorkspaces();
  const { attributes, isLoading } = useAttributes(sessionCurrentWorkspaceId, true);

  // 属性検索用のstate
  const [searchQuery, setSearchQuery] = useState("");

  // フィルタが設定されている属性を初期状態で展開する
  const initialExpandedAttributes = useMemo(() => {
    return new Set(Object.keys(attributeFilters));
  }, [attributeFilters]);

  const [expandedAttributes, setExpandedAttributes] = useState<Set<string>>(initialExpandedAttributes);

  // 検索クエリで属性をフィルタリング
  const filteredAttributes = useMemo(() => {
    if (!attributes) return undefined;
    if (!searchQuery.trim()) return attributes;
    return attributes.filter((attr) => attr.name.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [attributes, searchQuery]);

  const toggleAttribute = (attributeName: string) => {
    setExpandedAttributes((prev) => {
      const next = new Set(prev);
      if (next.has(attributeName)) {
        next.delete(attributeName);
      } else {
        next.add(attributeName);
      }
      return next;
    });
  };

  return (
    <ScrollArea className="h-full">
      {/* 検索ボックス */}
      <div className="relative mb-4 w-[300px]">
        <Input
          type="text"
          placeholder={t("chat.filter.searchPlaceholder")}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-3 pr-10 py-2 h-[36px] text-[14px] tracking-[0.28px] text-[#333] border border-[#b7b7b7] rounded-[5px] w-full"
        />
        <IconSearch
          className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 text-[#6888e1]"
          stroke={2}
        />
      </div>

      <div className="flex gap-6 pr-6 min-h-10">
        <div className="flex items-start pt-[10px] w-[60px]">
          <p className="text-[14px] font-semibold text-[#333333]">
            {t("chat.filter.attributes")}
          </p>
        </div>

        <div className="flex items-stretch self-stretch">
          <div className="w-[2px] bg-[#d9d9d9]" />
        </div>

        <AttributesList
          attributes={filteredAttributes}
          isLoading={isLoading}
          expandedAttributes={expandedAttributes}
          attributeFilters={attributeFilters}
          onToggleAttribute={toggleAttribute}
          onFilterChange={onUpdateAttributeFilter}
        />
      </div>

      <div className="mt-8">
        <VocSearchTagFilter
          tagIds={tagIds}
          onToggleTagId={onToggleTagId}
        />
      </div>
      <ScrollBar orientation="vertical" />
    </ScrollArea>
  );
};
